import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  Microphone, Play, ShieldCheck, Trash, PushPin, PushPinSlash,
  PlusCircle, SpotifyLogo, AppleLogo, YoutubeLogo, MusicNotes, Calendar,
  CircleNotch, Sparkle, Copy, CheckCircle, DownloadSimple, ArrowSquareOut,
  X,
} from "@phosphor-icons/react";
import {
  listPodcastEpisodes, createPodcastEpisode, deletePodcastEpisode,
  pinPodcastEpisode, generatePodcastScript,
} from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import ConsentEmbed from "@/components/ConsentEmbed";

function EpisodePlayer({ ep }) {
  if (ep.spotify_url) {
    return (
      <ConsentEmbed
        provider="spotify"
        title="Spotify-Player"
        aspect="audio"
        data-testid-suffix={ep.id}
      >
        {() => (
          <iframe
            src={ep.spotify_url}
            width="100%"
            height="152"
            frameBorder="0"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            title={ep.title}
            data-testid={`pod-player-${ep.id}`}
            className="rounded-sm"
          />
        )}
      </ConsentEmbed>
    );
  }
  if (ep.apple_url) {
    return (
      <ConsentEmbed
        provider="apple"
        title="Apple Podcasts-Player"
        aspect="audio"
        data-testid-suffix={ep.id}
      >
        {() => (
          <iframe
            src={ep.apple_url}
            width="100%"
            height="175"
            frameBorder="0"
            allow="autoplay *; encrypted-media *; fullscreen *"
            loading="lazy"
            title={ep.title}
            data-testid={`pod-player-${ep.id}`}
            className="rounded-sm"
          />
        )}
      </ConsentEmbed>
    );
  }
  if (ep.youtube_url) {
    return (
      <ConsentEmbed
        provider="youtube"
        title="YouTube-Video"
        aspect="video"
        data-testid-suffix={ep.id}
      >
        {() => (
          <div className="aspect-video">
            <iframe
              src={ep.youtube_url}
              width="100%"
              height="100%"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
              title={ep.title}
              data-testid={`pod-player-${ep.id}`}
              className="rounded-sm"
            />
          </div>
        )}
      </ConsentEmbed>
    );
  }
  if (ep.audio_url) {
    return (
      <audio
        controls
        preload="none"
        src={ep.audio_url}
        data-testid={`pod-player-${ep.id}`}
        className="w-full mt-1"
      >
        Dein Browser unterstützt das Audio-Tag nicht.
      </audio>
    );
  }
  return null;
}

function ScriptGenerator() {
  const [loading, setLoading] = useState(false);
  const [script, setScript] = useState(null);
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const generate = async () => {
    setLoading(true);
    setScript(null);
    try {
      const data = await generatePodcastScript();
      setScript(data);
      setOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Script-Generator fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(script.markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Text kopiert — ab zu NotebookLM!");
    } catch {
      toast.error("Konnte nicht kopieren — bitte manuell markieren.");
    }
  };

  const download = () => {
    const blob = new Blob([script.markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bodenblick-funk-dossier-${(script.generated_at || "").slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div
        className="eid-card p-5 space-y-3"
        data-testid="podcast-script-box"
        style={{ background: "linear-gradient(135deg, #141414 0%, #0E1A14 100%)" }}
      >
        <div className="flex items-center gap-2">
          <Sparkle size={16} className="text-[#00FA9A]" weight="fill" />
          <div className="eid-label">Script-Generator · für NotebookLM</div>
        </div>
        <p className="text-sm text-zinc-400 leading-relaxed">
          Ein Klick → wir aggregieren deine aktuellen Wochendaten (Märkte,
          Nachbarn-Liga, Bienen-Kalender) und veredeln sie via Gemini 3 Flash
          zu einem ~1500-Wort-Dossier. Kopier den Text in <strong>NotebookLM</strong>,
          generiere die Audio-Übersicht und lad die MP3 danach hier als neue Folge hoch.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={generate}
            disabled={loading}
            data-testid="podcast-script-generate"
            className="bg-[#00FA9A] text-black hover:bg-[#33FBAE] disabled:opacity-60 rounded-full px-4 py-2 text-sm flex items-center gap-1"
          >
            {loading ? (
              <><CircleNotch size={14} className="animate-spin" /> Script wird geschrieben …</>
            ) : (
              <><Sparkle size={14} weight="fill" /> Script aus Wochendaten erzeugen</>
            )}
          </button>
          {script && (
            <button
              onClick={() => setOpen(true)}
              data-testid="podcast-script-reopen"
              className="text-xs border border-[#00FA9A]/40 text-[#00FA9A] rounded-full px-3 py-1.5 hover:bg-[#00FA9A]/10"
            >
              Letztes Script anzeigen
            </button>
          )}
          <a
            href="https://notebooklm.google.com"
            target="_blank"
            rel="noreferrer"
            data-testid="podcast-script-notebooklm-link"
            className="text-xs text-[#00FA9A] hover:underline flex items-center gap-1 ml-auto"
          >
            NotebookLM öffnen <ArrowSquareOut size={10} />
          </a>
        </div>
      </div>

      {open && script && (
        <ScriptModal
          script={script}
          onClose={() => setOpen(false)}
          onCopy={copy}
          onDownload={download}
          copied={copied}
        />
      )}
    </>
  );
}

function ScriptModal({ script, onClose, onCopy, onDownload, copied }) {
  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="podcast-script-modal"
    >
      <div
        className="bg-[#121212] rounded-sm w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <div className="eid-label">Podcast-Script · {script.word_count} Wörter</div>
            <div className="text-[11px] font-mono text-zinc-500">
              Quelle: {script.source} · {(script.generated_at || "").slice(0, 16).replace("T", " ")}
            </div>
          </div>
          <button onClick={onClose} data-testid="podcast-script-modal-close" className="text-zinc-400 hover:text-white">
            <X size={16} />
          </button>
        </header>

        <div className="p-4 border-b border-white/10 bg-[#161616]">
          <div className="text-xs font-medium text-[#00FA9A] mb-1">
            So geht's in 3 Schritten
          </div>
          <ol className="text-xs text-zinc-400 space-y-1 list-decimal list-inside">
            <li>
              Klick <strong>„Text kopieren"</strong> unten.
            </li>
            <li>
              Öffne <a href="https://notebooklm.google.com" target="_blank" rel="noreferrer" className="underline text-[#00FA9A]">notebooklm.google.com</a>
              {" → "}Neues Notebook{" → "}„Quelle hinzufügen"{" → "}„Text einfügen" → einfügen → Hinzufügen
            </li>
            <li>
              Oben rechts „Audio-Übersicht generieren" → ~2 Min warten → MP3 laden und hier als neue Folge anlegen.
            </li>
          </ol>
        </div>

        <div className="flex items-center gap-2 p-3 border-b border-white/10">
          <button
            onClick={onCopy}
            data-testid="podcast-script-copy"
            className="bg-[#00FA9A] text-black hover:bg-[#33FBAE] rounded-full px-4 py-2 text-sm flex items-center gap-1"
          >
            {copied ? <><CheckCircle size={14} weight="fill" /> Kopiert!</> : <><Copy size={14} /> Text kopieren</>}
          </button>
          <button
            onClick={onDownload}
            data-testid="podcast-script-download"
            className="border border-white/10 hover:bg-white/5 rounded-full px-4 py-2 text-sm flex items-center gap-1"
          >
            <DownloadSimple size={14} /> Als .md herunterladen
          </button>
        </div>

        <div
          className="flex-1 overflow-y-auto p-4 font-mono text-[12px] whitespace-pre-wrap leading-relaxed bg-[#161616]"
          data-testid="podcast-script-content"
        >
          {script.markdown}
        </div>
      </div>
    </div>
  );
}

function AdminForm({ onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [spotify, setSpotify] = useState("");
  const [apple, setApple] = useState("");
  const [youtube, setYoutube] = useState("");
  const [audio, setAudio] = useState("");
  const [cover, setCover] = useState("");
  const [publishedAt, setPublishedAt] = useState("");
  const [pinned, setPinned] = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setTitle(""); setDescription(""); setSpotify(""); setApple("");
    setYoutube(""); setAudio(""); setCover(""); setPublishedAt("");
    setPinned(false); setAiGenerated(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Titel fehlt");
      return;
    }
    if (!spotify && !apple && !youtube && !audio) {
      toast.error("Mindestens einen Player-Link angeben (Spotify, Apple, YouTube oder MP3).");
      return;
    }
    setSaving(true);
    try {
      const res = await createPodcastEpisode({
        title: title.trim(),
        description: description.trim(),
        spotify_url: spotify.trim(),
        apple_url: apple.trim(),
        youtube_url: youtube.trim(),
        audio_url: audio.trim(),
        cover_url: cover.trim(),
        published_at: publishedAt || null,
        pinned,
        ai_generated: aiGenerated,
      });
      if (res.ok) {
        toast.success("Folge veröffentlicht");
        reset();
        onCreated();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Konnte Folge nicht speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="eid-card p-5 space-y-3" data-testid="pod-admin-form">
      <div className="flex items-center gap-2">
        <ShieldCheck size={16} className="text-[#00FA9A]" weight="fill" />
        <div className="eid-label">Admin · neue Folge</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <input
          value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Titel der Folge *"
          data-testid="pod-title"
          className="border border-white/10 rounded-sm px-3 py-2 text-sm"
        />
        <input
          type="date" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)}
          data-testid="pod-published-at"
          className="border border-white/10 rounded-sm px-3 py-2 text-sm"
        />
      </div>
      <textarea
        value={description} onChange={(e) => setDescription(e.target.value)}
        placeholder="Kurzbeschreibung (was lernst du hier?)"
        rows={3}
        data-testid="pod-description"
        className="w-full border border-white/10 rounded-sm px-3 py-2 text-sm"
      />
      <div className="eid-label mt-2">Player-Quelle (mind. eine)</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <input
          value={spotify} onChange={(e) => setSpotify(e.target.value)}
          placeholder="Spotify-URL (open.spotify.com/episode/…)"
          data-testid="pod-spotify"
          className="border border-white/10 rounded-sm px-3 py-2 text-sm"
        />
        <input
          value={apple} onChange={(e) => setApple(e.target.value)}
          placeholder="Apple Podcasts-URL"
          data-testid="pod-apple"
          className="border border-white/10 rounded-sm px-3 py-2 text-sm"
        />
        <input
          value={youtube} onChange={(e) => setYoutube(e.target.value)}
          placeholder="YouTube-URL (watch?v=… oder /shorts/…)"
          data-testid="pod-youtube"
          className="border border-white/10 rounded-sm px-3 py-2 text-sm"
        />
        <input
          value={audio} onChange={(e) => setAudio(e.target.value)}
          placeholder="Direkter MP3/OGG-Link (https://…)"
          data-testid="pod-audio"
          className="border border-white/10 rounded-sm px-3 py-2 text-sm"
        />
      </div>
      <input
        value={cover} onChange={(e) => setCover(e.target.value)}
        placeholder="Cover-Bild-URL (optional)"
        data-testid="pod-cover"
        className="w-full border border-white/10 rounded-sm px-3 py-2 text-sm"
      />
      <div className="flex items-center gap-3 flex-wrap">
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox" checked={pinned}
            onChange={(e) => setPinned(e.target.checked)}
            data-testid="pod-pinned"
          />
          Folge oben anpinnen
        </label>
        <label className="inline-flex items-center gap-2 text-sm bg-[#8B7CF6]/10 text-[#A99AFF] px-2 py-1 rounded-sm border border-[#8B7CF6]/40">
          <input
            type="checkbox" checked={aiGenerated}
            onChange={(e) => setAiGenerated(e.target.checked)}
            data-testid="pod-ai-generated"
          />
          KI-generiert (NotebookLM/Gemini)
        </label>
        <div className="flex-1" />
        <button
          type="submit" disabled={saving}
          data-testid="pod-submit"
          className="bg-[#00FA9A] text-black hover:bg-[#33FBAE] disabled:opacity-60 rounded-full px-5 py-2 text-sm flex items-center gap-1"
        >
          {saving ? (
            <><CircleNotch size={14} className="animate-spin" /> Speichern</>
          ) : (
            <><PlusCircle size={14} /> Folge veröffentlichen</>
          )}
        </button>
      </div>
    </form>
  );
}

function EpisodeCard({ ep, isAdmin, onChange }) {
  const remove = async () => {
    if (!window.confirm(`„${ep.title}" wirklich löschen?`)) return;
    await deletePodcastEpisode(ep.id);
    toast.success("Folge gelöscht");
    onChange();
  };
  const togglePin = async () => {
    const res = await pinPodcastEpisode(ep.id);
    toast.success(res.pinned ? "Angepinnt" : "Nicht mehr angepinnt");
    onChange();
  };
  const platforms = [
    ep.spotify_url && { icon: SpotifyLogo, label: "Spotify" },
    ep.apple_url && { icon: AppleLogo, label: "Apple" },
    ep.youtube_url && { icon: YoutubeLogo, label: "YouTube" },
    ep.audio_url && { icon: MusicNotes, label: "MP3" },
  ].filter(Boolean);

  return (
    <div className="eid-card p-5 relative" data-testid={`pod-ep-${ep.id}`}>
      {ep.pinned && (
        <div
          className="absolute -top-2 -left-2 bg-[#00FA9A] text-black text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm flex items-center gap-1"
          data-testid={`pod-ep-pinned-${ep.id}`}
        >
          <PushPin size={10} weight="fill" /> Empfehlung
        </div>
      )}
      {ep.ai_generated && (
        <div
          className="absolute -top-2 right-2 bg-[#3D2A78] text-white text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm flex items-center gap-1"
          data-testid={`pod-ep-ai-${ep.id}`}
          title="Diese Folge wurde mit Hilfe generativer KI (NotebookLM/Gemini) erstellt — bitte Inhalte selbst prüfen."
        >
          <Sparkle size={10} weight="fill" /> KI-generiert
        </div>
      )}
      <div className="flex gap-4 items-start">
        {ep.cover_url && (
          <img
            src={ep.cover_url} alt=""
            className="w-24 h-24 rounded-sm object-cover shrink-0 border border-white/10"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[11px] text-zinc-500">
            <Calendar size={11} />
            <span className="font-mono">
              {(ep.published_at || "").slice(0, 10)}
            </span>
            {ep.created_by_name && (
              <>
                <span>·</span>
                <span>{ep.created_by_name}</span>
              </>
            )}
          </div>
          <h2
            className="mt-1 text-xl font-medium tracking-tight"
            data-testid={`pod-ep-title-${ep.id}`}
          >
            {ep.title}
          </h2>
          {ep.description && (
            <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
              {ep.description}
            </p>
          )}
          {ep.ai_generated && (
            <p className="text-[11px] text-[#A99AFF] mt-2 leading-snug bg-[#8B7CF6]/10 border-l-2 border-[#8B7CF6]/40 pl-2 py-1" data-testid={`pod-ep-ai-note-${ep.id}`}>
              <strong>KI-Hinweis:</strong> Diese Folge wurde mit
              {" "}<strong>NotebookLM / Gemini</strong> automatisch aus
              Wochendaten erzeugt. Inhalte können Fehler enthalten und ersetzen
              keine Beratung — bitte selbst prüfen.
            </p>
          )}
          <div className="mt-2 flex items-center gap-2 flex-wrap text-[10px] text-zinc-400">
            {platforms.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/5 rounded-sm"
              >
                <Icon size={11} weight="fill" /> {label}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-4">
        <EpisodePlayer ep={ep} />
      </div>
      {isAdmin && (
        <div className="mt-3 flex gap-2 justify-end pt-3 border-t border-white/10">
          <button
            onClick={togglePin}
            data-testid={`pod-ep-pin-${ep.id}`}
            className="text-xs flex items-center gap-1 border border-white/10 hover:bg-white/5 rounded-sm px-3 py-1.5"
          >
            {ep.pinned ? <><PushPinSlash size={12} /> Unpin</> : <><PushPin size={12} /> Pin</>}
          </button>
          <button
            onClick={remove}
            data-testid={`pod-ep-delete-${ep.id}`}
            className="text-xs flex items-center gap-1 text-[#FF7A45] hover:bg-[#FF5500]/10 rounded-sm px-3 py-1.5"
          >
            <Trash size={12} /> Löschen
          </button>
        </div>
      )}
    </div>
  );
}

export default function Podcast() {
  const { user } = useAuth();
  const isAdmin = Boolean(user?.is_admin);
  const [episodes, setEpisodes] = useState(null);

  const load = async () => {
    try {
      const list = await listPodcastEpisodes();
      setEpisodes(list);
    } catch {
      setEpisodes([]);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="podcast-page">
      <Helmet>
        <title>Podcast · bodenblick.com</title>
      </Helmet>

      <div className="eid-card p-6 md:p-8 relative overflow-hidden" data-testid="podcast-hero">
        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            background:
              "radial-gradient(circle at 82% 18%, #0E1A14 0%, transparent 50%), radial-gradient(circle at 10% 92%, #141414 0%, transparent 50%)",
          }}
        />
        <div className="relative">
          <div className="flex items-center gap-2">
            <Microphone size={22} className="text-[#00FA9A]" weight="fill" />
            <div className="eid-label">Bodenblick-Funk</div>
          </div>
          <h1 className="mt-2 text-3xl md:text-4xl font-medium tracking-tight max-w-2xl">
            Geschichten vom Acker. Stimmen aus der Fläche.
          </h1>
          <p className="mt-3 text-sm md:text-base text-zinc-400 max-w-2xl leading-relaxed">
            Unser Podcast — kuratiert von der Bodenblick-Redaktion. Von Humusaufbau über
            Marktgeflüster bis zu Interviews mit Landwirt:innen, die Neues wagen. Hör zu,
            wo immer du gerade bist.
          </p>
          <div
            className="mt-4 flex items-start gap-2 max-w-2xl px-3 py-2 rounded-sm bg-[#8B7CF6]/10 border border-[#8B7CF6]/40 text-xs text-[#A99AFF] leading-snug"
            data-testid="podcast-ai-notice"
          >
            <span className="font-mono text-[10px] uppercase tracking-wider shrink-0 mt-0.5 px-1.5 py-0.5 bg-[#3D2A78] text-white rounded-sm">
              KI · NotebookLM
            </span>
            <span>
              <strong>Wichtig:</strong> Manche Folgen sind mit
              {" "}<strong>Google NotebookLM / Gemini</strong> automatisch aus
              Wochendaten erzeugt. Sie ersetzen keine redaktionelle Quelle und
              keine Beratung. Inhalte können Fehler enthalten — bitte vor
              jeder Nutzung <strong>eigenständig prüfen</strong> (EU-KI-VO Art. 50).
            </span>
          </div>
          {isAdmin ? (
            <div
              className="mt-3 inline-flex items-center gap-1 text-xs text-[#00FA9A] bg-[#00FA9A]/10 px-2 py-0.5 rounded-sm"
              data-testid="podcast-admin-badge"
            >
              <ShieldCheck size={12} weight="fill" /> Du bist Admin · du kannst veröffentlichen
            </div>
          ) : (
            <div className="mt-3 inline-flex items-center gap-1 text-xs text-zinc-400 bg-white/5 px-2 py-0.5 rounded-sm">
              <Play size={12} weight="fill" /> Hör-Modus · nur Admins veröffentlichen
            </div>
          )}
        </div>
      </div>

      {isAdmin && <ScriptGenerator />}
      {isAdmin && <AdminForm onCreated={load} />}

      {episodes === null && (
        <div className="eid-card p-6 flex items-center gap-3">
          <CircleNotch size={16} className="animate-spin text-[#00FA9A]" /> Lade Folgen …
        </div>
      )}

      {episodes && episodes.length === 0 && (
        <div className="eid-card p-8 text-center" data-testid="podcast-empty">
          <Microphone size={28} className="mx-auto text-[#00FA9A]" weight="fill" />
          <div className="mt-2 font-medium">Noch keine Folgen</div>
          <div className="text-sm text-zinc-400 mt-1">
            {isAdmin
              ? "Leg los — veröffentliche oben deine erste Folge."
              : "Bald gibt's hier Geschichten vom Acker. Schau wieder vorbei."}
          </div>
        </div>
      )}

      {episodes && episodes.length > 0 && (
        <div className="space-y-4" data-testid="podcast-list">
          {episodes.map((ep) => (
            <EpisodeCard key={ep.id} ep={ep} isAdmin={isAdmin} onChange={load} />
          ))}
        </div>
      )}
    </div>
  );
}
